import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { CampaignList } from "src/app/features/Campaign/types/CampaignList";
import { CAMPAIGN_STATE } from "src/app/features/Campaign/types/CampaignState";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayCampaignUpdateStatusValue,
    displayDateTimeValue,
    displayEnumValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const CampaignsTable: FC<ResourceTableProps<CampaignList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<CampaignList>): ExtendedColumnsType<CampaignList>[] => {
            return [
                {
                    title: t("campaignList.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("campaignList.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("campaignList.type"),
                    key: "type",
                    dataIndex: "type",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "type" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "campaign-types"),
                },
                {
                    title: t("campaignList.updateEftVersion"),
                    key: "updateType",
                    dataIndex: "updateType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "updateType" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "campaign-update-types"),
                },
                {
                    title: t("campaignList.numberOfPhases"),
                    key: "numberOfPhases",
                    dataIndex: "numberOfPhases",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "numberOfPhases" ? sortInfo.order : null,
                },
                {
                    title: t("campaignList.validFrom"),
                    key: "validFrom",
                    dataIndex: "validFrom",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "validFrom" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
                {
                    title: t("campaignList.validTo"),
                    key: "validTo",
                    dataIndex: "validTo",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "validTo" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
                {
                    title: t("genericTable.column.status"),
                    key: "state",
                    dataIndex: "state",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "state" ? sortInfo.order : null,
                    render: (value) => displayCampaignUpdateStatusValue(value),
                },
                {
                    title: t("campaignList.owner"),
                    key: "campaignOwner",
                    dataIndex: "campaignOwner",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "campaignOwner" ? sortInfo.order : null,
                },
                {
                    title: t("genericTable.column.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: CampaignList) => {
        const action = record.state === CAMPAIGN_STATE.CREATED ? FORM_ACTION.EDIT : FORM_ACTION.VIEW;
        // Determine variant based on createByTerminals field
        const variant = record.createByTerminals ? ResourceVariant.TERMINALS : ResourceVariant.ORG_UNITS;
        navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}/${variant}/${action}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<CampaignList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="campaigns_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: CampaignList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
